const STEP_TRANSITION_MS = 200;

export default function initializeMemberAuth() {
    document.querySelectorAll('[data-member-auth-modal]').forEach(function (modal) {
        new MemberAuthModal(modal);
    });
}

class MemberAuthModal {
    constructor(modal) {
        this.modal = modal;
        this.emailStep = modal.querySelector('[data-auth-step="email"]');
        this.sentStep = modal.querySelector('[data-auth-step="sent"]');
        this.pinStep = modal.querySelector('[data-auth-step="pin"]');
        this.emailInput = modal.querySelector('[data-auth-email]');
        this.emailForm = modal.querySelector('[data-auth-email-form]');
        this.pinForm = modal.querySelector('[data-auth-pin-form]');
        this.pinInputs = Array.from(modal.querySelectorAll('[data-auth-pin-digit]'));
        this.pinError = modal.querySelector('[data-auth-pin-error]');
        this.emailError = modal.querySelector('[data-auth-email-error]');
        this.pinSubmit = modal.querySelector('[data-auth-pin-submit]');
        this.countdownEl = modal.querySelector('[data-auth-countdown]');
        this.resendBtn = modal.querySelector('[data-auth-resend]');
        this.skipSentBtn = modal.querySelector('[data-auth-skip-sent]');
        this.closeBtns = modal.querySelectorAll('[data-auth-close]');

        this.email = '';
        this.pendingRedirect = null;
        this.currentStepEl = null;
        this.stepLeaveTimeout = null;
        this.sentStepTimeout = null;
        this.pendingExpiresIn = 180;
        this.countdownInterval = null;
        this.countdownEndAt = null;
        this.loading = false;
        this.apiBase = (window.wpApiSettings?.root || '/wp-json/') + 'venti/v1/members/auth/';
        this.nonce = window.wpApiSettings?.nonce || '';

        this.init();
    }

    init() {
        document.querySelectorAll('[data-open-member-auth]').forEach((trigger) => {
            trigger.addEventListener('click', (e) => {
                e.preventDefault();
                if (!this.isPinPending()) this.pendingRedirect = null;
                this.open();
            });

            trigger.addEventListener('keydown', (e) => {
                if (e.key !== 'Enter' && e.key !== ' ') return;
                e.preventDefault();
                if (!this.isPinPending()) this.pendingRedirect = null;
                this.open();
            });
        });

        document.addEventListener('venti:open-member-auth', (e) => {
            if (!this.isPinPending()) this.pendingRedirect = e.detail?.link || null;
            this.open();
        });

        this.closeBtns.forEach((btn) => btn.addEventListener('click', () => this.close()));

        this.modal.addEventListener('click', (e) => {
            if (e.target === this.modal) this.close();
        });

        if (this.emailForm) {
            this.emailInput?.addEventListener('invalid', () => this.showError('email', 'email-invalid'));
            this.emailForm.addEventListener('submit', (e) => {
                e.preventDefault();
                this.requestPin();
            });
        }

        if (this.pinForm) {
            this.pinForm.addEventListener('submit', (e) => {
                e.preventDefault();
                this.verifyPin();
            });
        }

        if (this.resendBtn) {
            this.resendBtn.addEventListener('click', () => this.requestPin());
        }

        if (this.skipSentBtn) {
            this.skipSentBtn.addEventListener('click', () => this.goToPinStep());
        }

        this.pinInputs.forEach((input, index) => {
            input.addEventListener('input', () => {
                input.value = input.value.replace(/\D/g, '').slice(0, 1);

                if (input.value && this.pinInputs[index + 1]) {
                    this.pinInputs[index + 1].focus();
                }

                if (this.pinInputs.every((i) => i.value)) {
                    this.verifyPin();
                }
            });

            input.addEventListener('keydown', (e) => {
                if (e.key === 'Backspace' && !input.value && this.pinInputs[index - 1]) {
                    this.pinInputs[index - 1].focus();
                }
            });

            input.addEventListener('paste', (e) => this.handlePinPaste(e));
        });

    }

    handlePinPaste(e) {
        const text = (e.clipboardData || window.clipboardData)?.getData('text') || '';
        const digits = text.replace(/\D/g, '').slice(0, this.pinInputs.length);
        if (!digits) return;

        e.preventDefault();

        digits.split('').forEach((digit, i) => {
            if (this.pinInputs[i]) this.pinInputs[i].value = digit;
        });

        const nextIndex = Math.min(digits.length, this.pinInputs.length - 1);
        this.pinInputs[nextIndex]?.focus();

        if (this.pinInputs.every((i) => i.value)) {
            this.verifyPin();
        }
    }

    isPinPending() {
        return !!this.countdownEndAt;
    }

    open() {
        this.modal.classList.add('is-open');
        document.body.classList.add('member-auth-modal-open');

        // If a PIN was already requested and is still valid, jump straight back to
        // the PIN step instead of the email form - closing/reopening the modal must
        // never be a way to trigger another PIN send.
        if (this.isPinPending()) {
            clearTimeout(this.sentStepTimeout);
            this.sentStepTimeout = null;
            this.showStep('pin');
            this.pinInputs.find((i) => !i.value)?.focus();
            return;
        }

        this.showStep('email');
        this.emailInput?.focus();
    }

    close() {
        this.modal.classList.remove('is-open');
        document.body.classList.remove('member-auth-modal-open');

        // Keep the in-progress PIN step (and its countdown) alive in the background
        // so re-opening resumes it - only reset back to the email form once there is
        // no active/valid PIN request to resume. The "sent" confirmation counts too,
        // since it's still mid-flow towards a PIN that's already been issued.
        if (this.loading || this.isPinPending() || this.sentStepTimeout) {
            return;
        }

        // Reset instantly (no transition) so a later re-open never briefly flashes
        // whatever step was last shown (e.g. the PIN fields) before settling on email.
        clearTimeout(this.stepLeaveTimeout);
        this.stopCountdown();
        [this.emailStep, this.sentStep, this.pinStep].forEach((el) => {
            if (!el) return;
            el.classList.remove('is-active');
            el.hidden = el !== this.emailStep;
        });
        this.emailStep?.classList.add('is-active');
        this.currentStepEl = this.emailStep;

        this.pinInputs.forEach((i) => (i.value = ''));
        this.setPinDisabled(false);
        this.clearError('pin');
        this.clearError('email');
    }

    startCountdown(seconds) {
        this.stopCountdown();
        this.setPinDisabled(false);
        this.clearError('pin');

        // While a code is outstanding, "Wyślij ponownie" is replaced by the countdown
        // itself - it only reappears once the current code has actually expired, so it
        // can't be used to spam new codes while a valid one is still waiting to be used.
        if (this.resendBtn) this.resendBtn.hidden = true;

        this.countdownEndAt = Date.now() + seconds * 1000;
        this.tickCountdown();
        this.countdownInterval = window.setInterval(() => this.tickCountdown(), 250);
    }

    stopCountdown() {
        clearInterval(this.countdownInterval);
        this.countdownInterval = null;
        this.countdownEndAt = null;
        if (this.countdownEl) this.countdownEl.textContent = '';
    }

    tickCountdown() {
        if (!this.countdownEndAt) return;

        const remainingMs = this.countdownEndAt - Date.now();

        if (remainingMs <= 0) {
            clearInterval(this.countdownInterval);
            this.countdownInterval = null;
            if (this.countdownEl) this.countdownEl.textContent = '';
            this.handlePinExpired();
            return;
        }

        const totalSeconds = Math.ceil(remainingMs / 1000);
        const minutes = Math.floor(totalSeconds / 60);
        const seconds = String(totalSeconds % 60).padStart(2, '0');
        const label = `${minutes}:${seconds}`;

        if (this.countdownEl) {
            this.countdownEl.textContent = this.message('countdown').replace('%s', label);
            this.countdownEl.classList.toggle('is-ending', totalSeconds <= 30);
        }
    }

    handlePinExpired() {
        this.setPinDisabled(true);
        this.pinInputs.forEach((i) => (i.value = ''));

        if (this.resendBtn) this.resendBtn.hidden = false;

        this.showError('pin', 'pin-expired');
    }

    setPinDisabled(disabled) {
        this.pinInputs.forEach((i) => (i.disabled = disabled));
        if (this.pinSubmit) this.pinSubmit.disabled = disabled;
    }

    showStep(step) {
        const map = { email: this.emailStep, sent: this.sentStep, pin: this.pinStep };
        const nextEl = map[step];
        if (!nextEl || nextEl === this.currentStepEl) return;

        clearTimeout(this.stepLeaveTimeout);

        const revealNext = () => {
            nextEl.hidden = false;
            // force reflow so the opacity/transform transition actually plays
            void nextEl.offsetWidth;
            nextEl.classList.add('is-active');
            this.currentStepEl = nextEl;
        };

        const leavingEl = this.currentStepEl;
        if (leavingEl) {
            // Fade the old step out completely before the new one appears, so they
            // never overlap in the document flow (that overlap is what caused the
            // box to visibly jump/resize mid-transition).
            leavingEl.classList.remove('is-active');
            this.stepLeaveTimeout = window.setTimeout(() => {
                leavingEl.hidden = true;
                revealNext();
            }, STEP_TRANSITION_MS);
        } else {
            revealNext();
        }
    }

    setLoading(isLoading) {
        this.loading = isLoading;
        this.modal.classList.toggle('is-loading', isLoading);
    }

    message(key) {
        return this.modal.getAttribute('data-auth-message-' + key) || '';
    }

    clearError(step) {
        this.updateError(step, '');
    }

    showError(step, key) {
        this.updateError(step, this.message(key));
    }

    updateError(step, text) {
        const region = step === 'email' ? this.emailError : this.pinError;
        const inputs = step === 'email' ? [this.emailInput] : this.pinInputs;
        if (!region) return;
        inputs.filter(Boolean).forEach((input) => {
            const descriptions = (input.getAttribute('aria-describedby') || '').split(/\s+/).filter((id) => id && id !== region.id);
            if (text) {
                descriptions.push(region.id);
                input.setAttribute('aria-invalid', 'true');
            } else {
                input.removeAttribute('aria-invalid');
            }
            if (descriptions.length) input.setAttribute('aria-describedby', descriptions.join(' '));
            else input.removeAttribute('aria-describedby');
        });
        region.hidden = !text;
        region.textContent = text;
    }

    async requestPin() {
        if (this.loading || this.sentStepTimeout || (this.countdownEndAt && this.countdownEndAt > Date.now())) return;
        const errorStep = this.currentStepEl === this.pinStep ? 'pin' : 'email';
        this.clearError(errorStep);
        this.email = (this.emailInput?.value || '').trim();
        if (!this.email || !this.emailInput?.checkValidity()) {
            this.showError(errorStep, 'email-invalid');
            return;
        }

        this.setLoading(true);

        try {
            const response = await fetch(this.apiBase + 'request-pin', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': this.nonce,
            },
            body: JSON.stringify({ email: this.email, target_url: this.pendingRedirect || window.location.href }),
            });
            const data = await response.json();
            if (!response.ok || data?.success !== true || !Number.isFinite(data?.expires_in) || data.expires_in <= 0) {
                const key = response.status === 429 ? 'rate-limited' :
                    (response.status === 400 && data?.code === 'invalid_email' ? 'email-invalid' : 'request-failed');
                this.showError(errorStep, key);
                return;
            }
                this.showStep('sent');
                this.pinInputs.forEach((i) => (i.value = ''));
                this.pendingExpiresIn = data.expires_in;
                this.startCountdown(this.pendingExpiresIn);
                clearTimeout(this.sentStepTimeout);
                this.sentStepTimeout = window.setTimeout(() => this.goToPinStep(), 2600);
        } catch (_) {
            this.showError(errorStep, 'request-failed');
        } finally {
            this.setLoading(false);
        }
    }

    // Moves on from the "message sent" confirmation to the PIN entry step.
    // Called either by the auto-advance timeout or the "Przejdź dalej" button,
    // whichever happens first - clearing the timeout keeps them from double-firing.
    goToPinStep() {
        clearTimeout(this.sentStepTimeout);
        this.sentStepTimeout = null;
        this.showStep('pin');
        this.pinInputs[0]?.focus();
    }

    async verifyPin() {
        if (this.loading) return;
        this.clearError('pin');
        if (this.countdownEndAt && this.countdownEndAt <= Date.now()) {
            this.handlePinExpired();
            return;
        }
        const pin = this.pinInputs.map((i) => i.value).join('');
        if (!/^\d{6}$/.test(pin)) {
            this.showError('pin', 'pin-incomplete');
            this.pinInputs.find((i) => !i.value)?.focus();
            return;
        }

        this.setLoading(true);

        try {
            const response = await fetch(this.apiBase + 'verify-pin', {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-WP-Nonce': this.nonce,
            },
            body: JSON.stringify({ email: this.email, pin }),
            });
            const data = await response.json();
                if (response.ok && data?.success === true) {
                    if (this.pendingRedirect) {
                        window.location.href = this.pendingRedirect;
                    } else {
                        window.location.reload();
                    }
                    return;
                }

            const invalid = response.status === 400 && data?.success === false;
            this.showError('pin', response.status === 429 ? 'rate-limited' : (invalid ? 'pin-invalid' : 'request-failed'));
            if (invalid) {
                this.pinInputs.forEach((i) => (i.value = ''));
                this.pinInputs[0]?.focus();
            }
        } catch (_) {
            this.showError('pin', 'request-failed');
        } finally {
            this.setLoading(false);
        }
    }
}
